import { useState, useMemo } from 'react';
import { ColumnList } from './ColumnManager';

/**
 * Telemetry display picker — opened from the gear icon in the
 * TelemetryPanel header. Reuses ColumnManager for the card list and
 * adds a "Layout" segmented toggle at the top.
 *
 * Props:
 *  - availableCards : [{ id, label, locked? }]  cards the current
 *      selection exposes (vehicle keys when a vehicle is selected,
 *      staff keys when staff, union when nothing is selected).
 *  - layout         : 'floating' | 'docked' | 'side'
 *  - setLayout      : (string) => void
 *  - visibleCards   : Record<string, boolean>  pref source of truth
 *  - isCardVisible  : (cardKey) => boolean     optional; the panel's own predicate
 *      (honours default-hidden cards). Falls back to visibleCards.
 *  - cardOrder      : string[]                 pref source of truth
 *  - setVisibility  : (cardKey, boolean) => void
 *  - setCardOrder   : (string[]) => void
 *  - onReset        : () => void
 *  - onClose        : () => void
 *  - t              : translation function
 */
// Static keys so scripts/check-i18n.js can see them (the old
// `telemetry.layout.${mode}` template never resolved — `telemetry.layout`
// is the section label string, so every locale showed the English
// fallback).
const LAYOUT_OPTIONS = [
  ['floating', 'telemetry.layoutFloating', 'Floating'],
  ['docked', 'telemetry.layoutDocked', 'Docked'],
  ['side', 'telemetry.layoutSide', 'Side'],
];

export default function TelemetryCardPicker({
  availableCards,
  layout,
  setLayout,
  visibleCards,
  isCardVisible,
  cardOrder,
  setVisibility,
  setCardOrder,
  onReset,
  onClose,
  t,
}) {
  const [dragIdx, setDragIdx] = useState(null);

  // Build the columns array ColumnManager expects from availableCards
  // + the persisted cardOrder. Unknown new cards auto-append.
  const orderedColumns = useMemo(() => {
    const byId = new Map(availableCards.map((c) => [c.id, c]));
    const inOrder = [];
    cardOrder.forEach((id) => {
      if (byId.has(id)) {
        inOrder.push(byId.get(id));
        byId.delete(id);
      }
    });
    byId.forEach((c) => inOrder.push(c));
    return inOrder;
  }, [availableCards, cardOrder]);

  // ColumnManager's `visible` prop is string[]. Convert our
  // "missing key = visible" Record convention to the array.
  const visibleIds = useMemo(
    () => orderedColumns.filter((c) => (isCardVisible ? isCardVisible(c.id) : visibleCards[c.id] !== false)).map((c) => c.id),
    [orderedColumns, visibleCards, isCardVisible],
  );

  // ColumnManager calls setVisible with a functional updater:
  //   setVisible((prev) => newArray)
  // We intercept by providing a setter that receives the updater fn,
  // evaluates it against the current visibleIds, then diffs to find
  // which cards toggled so we can call our setVisibility(id, bool).
  const handleSetVisible = (updaterOrArray) => {
    const nextVisibleIds =
      typeof updaterOrArray === 'function'
        ? updaterOrArray(visibleIds)
        : updaterOrArray;

    const wasVisible = new Set(visibleIds);
    const isVisible = new Set(nextVisibleIds);

    // Cards that flipped off
    wasVisible.forEach((id) => {
      if (!isVisible.has(id)) setVisibility(id, false);
    });
    // Cards that flipped on
    isVisible.forEach((id) => {
      if (!wasVisible.has(id)) setVisibility(id, true);
    });
  };

  return (
    <div
      className="rounded-xl p-3 space-y-3 text-xs"
      style={{
        background: 'rgba(15,23,42,0.94)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--glass-border)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        minWidth: 240,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-white font-semibold">
          {t('telemetry.displaySettings', 'Telemetry display')}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-white/60 hover:text-white text-xs"
          aria-label={t('common.close', 'Close')}
        >
          ✕
        </button>
      </div>

      {/* Layout segmented control */}
      <div>
        <div className="text-white/60 mb-1">{t('telemetry.layout', 'Layout')}</div>
        <div className="flex rounded-lg overflow-hidden border border-white/15">
          {LAYOUT_OPTIONS.map(([mode, key, fallback]) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setLayout(mode);
                // Close the picker so the user sees clear feedback —
                // otherwise the picker's host panel unmounts (floating
                // → docked) and the popover just vanishes, looking
                // like nothing happened.
                onClose();
              }}
              aria-pressed={layout === mode}
              className={`flex-1 py-1.5 text-[11px] font-medium transition-colors ${
                layout === mode
                  ? 'bg-blue-500/40 text-white'
                  : 'bg-transparent text-white/60 hover:bg-white/10'
              }`}
            >
              {t(key, fallback)}
            </button>
          ))}
        </div>
      </div>

      {/* Card visibility + order — inline ColumnList nested in a
          light-glass card so row text colors (slate-700) stay
          readable against the dark picker chrome. */}
      <div>
        <div className="text-white/60 mb-1">{t('telemetry.cards', 'Cards')}</div>
        <div
          className="rounded-lg overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.92)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <ColumnList
            columns={orderedColumns}
            visible={visibleIds}
            setVisible={handleSetVisible}
            dragIdx={dragIdx}
            setDragIdx={setDragIdx}
            setColOrder={setCardOrder}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end pt-1 border-t border-white/10">
        <button
          type="button"
          onClick={onReset}
          className="text-[10px] text-white/60 hover:text-white transition-colors"
        >
          {t('telemetry.resetDefaults', 'Reset to defaults')}
        </button>
      </div>
    </div>
  );
}
